/* eslint-disable react/display-name */
/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react';
import {useContext} from 'react';
import {TPAComponentsProvider} from '@wix/wix-ui-tpa/cssVars';
import {useSettings, useStyles} from '@wix/tpa-settings/react';
import settingsParams from './components/GridGallery/settingsParams';
import {useStylesParams} from './stylesParamsContext';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {IGlobals} from './gallery/galleryGlobalStrategy';
import {Experiments} from './constants';
import {resolveMultilingualText} from './resolveMultilingualText';

let Provider;
let Consumer;
let globalsContext;
let strategy: (props: any) => {globals: any; locals: any};

export type Strategy<T> = (props: any) => {globals: T; locals: any};

export function withGlobalsProvider(Component, sliderGallery) {
  return (props) => {
    const {
      globals,
      locals,
      globals: {shouldShowMobile, isMobile, isRTL},
    } = strategy(props);

    globals.styles = useStyles();
    globals.stylesParams = useStylesParams();
    globals.useExperiments = useExperiments().experiments;
    const isAddSliderGalleryTitleToGlobalPropsContextExperimentEnabled = globals.useExperiments.enabled(
      Experiments.AddSliderGalleryTitleToGlobalPropsContext
    );
    const isFixMultilingualTextInSFEnabled = globals.useExperiments.enabled(Experiments.FixMultilingualTextInSF);
    const isFixOosTextLiveUpdateEnabled = globals.useExperiments.enabled(Experiments.FixOosTextLiveUpdateOnStage);
    const isPrimaryLanguage = globals.isPrimaryLanguage ?? true;

    if (globals.textsMap) {
      const {get: getSettings} = useSettings();

      const resolveText = (textsMapValue: string, settingsParam) =>
        resolveMultilingualText(
          textsMapValue,
          getSettings(settingsParam),
          isFixMultilingualTextInSFEnabled,
          isPrimaryLanguage
        );

      if (!sliderGallery) {
        globals.textsMap.loadMoreButtonText = resolveText(
          globals.textsMap.loadMoreButtonText,
          settingsParams.LOAD_MORE_BUTTON
        );
        globals.textsMap.loadPreviousButtonText = resolveText(
          globals.textsMap.loadPreviousButtonText,
          settingsParams.gallery_loadPreviousText
        );
        globals.textsMap.filtersTitleText = resolveText(
          globals.textsMap.filtersTitleText,
          settingsParams.FILTERS_MAIN_TITLE
        );
        globals.textsMap.sortTitleText = resolveText(globals.textsMap.sortTitleText, settingsParams.SORTING_MAIN_TITLE);
        globals.textsMap.sortLabel = resolveText(globals.textsMap.sortLabel, settingsParams.SORTING_MAIN_TITLE);
      }

      globals.textsMap.addToCartOutOfStock = resolveText(
        globals.textsMap.addToCartOutOfStock,
        settingsParams.gallery_oosButtonText
      );
      if (isFixOosTextLiveUpdateEnabled) {
        globals.textsMap.productOutOfStockText = resolveText(
          globals.textsMap.productOutOfStockText,
          settingsParams.gallery_oosButtonText
        );
      }
      globals.textsMap.galleryAddToCartButtonText = resolveText(
        globals.textsMap.galleryAddToCartButtonText,
        settingsParams.gallery_addToCartText
      );
      globals.textsMap.galleryAddToCartPreOrderButtonText = resolveText(
        globals.textsMap.galleryAddToCartPreOrderButtonText,
        settingsParams.gallery_preOrderText
      );
      globals.textsMap.categoryTreeTitle = resolveText(
        globals.textsMap.categoryTreeTitle,
        settingsParams.categoryTreeTitle
      );
      globals.textsMap.selectOptionsButtonText = resolveText(
        globals.textsMap.selectOptionsButtonText,
        settingsParams.gallery_selectOptionsButtonText
      );
      globals.textsMap.categoryListShowMore = resolveText(
        globals.textsMap.categoryListShowMore,
        settingsParams.gallery_categoryFooterDescriptionShowMoreButtonText
      );
      globals.textsMap.categoryListShowLess = resolveText(
        globals.textsMap.categoryListShowLess,
        settingsParams.gallery_categoryFooterDescriptionShowLessButtonText
      );

      /* istanbul ignore else: related to Editor 3 live text editing for the slider title, hard to test */
      if (isAddSliderGalleryTitleToGlobalPropsContextExperimentEnabled) {
        globals.textsMap.sliderGalleryTitle = resolveText(
          globals.textsMap.sliderGalleryTitle,
          settingsParams.SLIDER_GALLERY_TITLE_COLLECTION
        );
      }
    }

    return (
      <Provider value={globals}>
        <TPAComponentsProvider value={{mobile: shouldShowMobile || isMobile, rtl: isRTL}}>
          <Component {...locals} />
        </TPAComponentsProvider>
      </Provider>
    );
  };
}

export const withGlobals = (Component): any => {
  return (props) => {
    return <Consumer>{(globals) => <Component globals={globals} {...props} ref={props.innerRef} />}</Consumer>;
  };
};

export function useGlobals() {
  return useContext<IGlobals>(globalsContext);
}

export function initContext<T>(aStrategy: Strategy<T>) {
  globalsContext = React.createContext<T>(undefined);
  strategy = aStrategy;
  Provider = globalsContext.Provider;
  Consumer = globalsContext.Consumer;
}
